import { mount } from 'svelte';
import './app.css';
import App from './App.svelte';
import { initTheme } from '$lib/ui/theme.svelte';
import { applyTv } from '$lib/ui/tv';

/*
 * BEFORE ANYTHING IS DRAWN. A television needs a different layout viewport and a
 * safe margin off every edge, and both have to be settled while `<body>` is
 * still empty: `data-tv` decides which rules match on the first pass, and
 * rewriting the viewport meta over a painted page reflows it in front of the
 * viewer. See `tv.ts` for what a television is and why no media query can say.
 */
applyTv();

/*
 * And the palette, for the same reason: a theme written after mount paints
 * Rouge for one frame and then the chosen colours over it, which is a flash of
 * the wrong app on every cold start.
 */
initTheme();

/*
 * iOS SAFARI'S SHARE OF THE ZOOM BAN. Safari deliberately ignores
 * `user-scalable=no` and `maximum-scale` (an accessibility stance a public
 * site should respect — this is a private app whose owner asked for the
 * opposite: accidental pinches ruin the layout). Pinch reaches the page as
 * Safari's proprietary gesture events before any touch-action arbitration,
 * so cancelling them here, non-passively, is the one reliable stop.
 */
for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
  window.addEventListener(type, (e) => e.preventDefault(), { passive: false });
}

/*
 * A STALE TAB MUST HEAL ITSELF. Every deploy replaces every hashed chunk, and
 * a phone that kept the app open across a deploy holds the OLD file list — its
 * first lazy import after that (pressing play is the big one: the player is
 * deliberately not in the boot bundle) asks for a chunk that no longer exists,
 * and without this the failure surfaced as Watch's scaffold-era "the player is
 * not in this build" screen on perfectly healthy phones (two owner reports,
 * 2026-08-17; a browser restart "fixed" it, which is exactly the tell).
 * Vite announces precisely this failure; one guarded reload fetches the fresh
 * build and replays the same URL. The guard stops a reload loop when the
 * failure is real (offline, server down): at most one automatic reload per
 * minute — past that the failure propagates to the surface that asked, which
 * shows its own honest words.
 */
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'jt.chunk-reload.v1';
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY)) || 0;
  } catch {
    /* private mode: reload unguarded once is still better than a dead player */
  }
  if (Date.now() - last < 60_000) return; // let it propagate
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* fine */
  }
  event.preventDefault();
  location.reload();
});

/*
 * THE SHELL CACHE. Registered in production only, and only where the browser
 * offers one. web/public/sw.js carries the whole argument: network-first,
 * shell-only, keyed to this build - so it can open with the wifi off (which is
 * what makes a download reachable) without ever serving a stale build while a
 * network exists.
 *
 * Failure here is silent on purpose: a browser that refuses a worker (private
 * mode, an enterprise policy, a Safari tab that is not installed) is a browser
 * that simply has no offline start, and every other thing the app does still
 * works.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

const target = document.getElementById('app');
if (!target) throw new Error('#app mount point missing from index.html');

const app = mount(App, { target });

export default app;
